import { UseMutateAsyncFunction, useQueryClient, useMutation } from "@tanstack/react-query";
import { CompanyDetailsData } from "../../custom-types/user";
import API from "../const";
import { QueryOptions } from "../type";
import { getAuthIdParam } from "../../methods/getAuthIdParam";
import { getAuthHeader } from "../utils/getAuthHeader";
import { handleMutateResponse } from "../utils/handleMutateResponse";
import { QueryNames } from "./queryNames";
import { validateAndConvertReqParamToInt } from "_services/utils/validateAndConvertReqParamToNum";
import { useVahakQuery } from "../../hooks/useVahakQueryClient";

// Used by app
// [
//     load_routes,
//     material_types,
//     lorry_types,
//     recommended_count,
//     alternate_numbers,
//     company_city_routes,
//     trip_details,
//     agent_details
// ];

/*
 * Get User Profile data
 */
interface UserProfileServiceQueryParams {
    companyId: number | string | undefined;
}

export function useGetUserProfileDetails({ companyId }: UserProfileServiceQueryParams, options?: QueryOptions) {
    const authHeader = getAuthHeader();
    let preloads = authHeader?.token ? "&preload=agent_details&preload=material_types" : "";
    const queryClient = useQueryClient();
    return useVahakQuery([QueryNames.userProfile, companyId], () => {
            const validatedCompanyId = validateAndConvertReqParamToInt("companyId", companyId);
            if (!validatedCompanyId) {
                return new Promise((res) => res(undefined));
            }
            return fetch(`${API.USER_PROFILE_DETAILS}&c_i=${validatedCompanyId}${preloads}`, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...authHeader
                }
            })
                .then((res) => {
                    if (res.status === 401) {
                        queryClient.invalidateQueries(QueryNames.validateToken);
                        throw new Error("Some Error Occurred");
                    }
                    if (res.status >= 400) {
                        throw new Error("Some Error Occurred");
                    } else {
                        return res.json().then((jsonData) => {
                            const allData = jsonData.data;
                            return allData;
                        });
                    }
                })
                .catch(() => {
                    throw new Error("Some Error Occurred");
                });
        }, {
            ...options,
            onSuccess: options?.onSuccess ?? (() => {}),
            onError: options?.onError ?? (() => {})
        } as any);
}

interface UpdateCompanyDetailsParams {
    companyId: string | number | undefined;
    languageCode?: number;
    companyName?: string;
    serviceType?: string;
    placeId?: string;
    logo?: string;
    routeIds?: number[];
    is_wa_optin?: boolean;
    coverPicture?: string;
    companyBio?: string;
    establishedIn?: number;
    mandateKyc?: number;
    aadhaarVerifiedStatus?: boolean;
}

// To Update Company Details
export function useUpdateCompanyDetails() {
    const queryClient = useQueryClient();

    const apiClient = `${API.UPDATE_COMPANY_DETAILS}${getAuthIdParam()}`;

    // Removed data and error props to remove warning, can be addded when need
    const { isPending: isLoading, isError, isSuccess, mutateAsync } = useMutation({ mutationFn: ({
            companyId,
            languageCode,
            companyName,
            serviceType,
            placeId,
            logo,
            routeIds,
            is_wa_optin,
            coverPicture,
            companyBio,
            establishedIn,
            mandateKyc,
            aadhaarVerifiedStatus
        }: UpdateCompanyDetailsParams) =>
            fetch(apiClient, {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: JSON.stringify({
                    id: parseInt(companyId as string),
                    language_code: languageCode,
                    name: companyName?.trim(),
                    service_type: serviceType,
                    place_id: placeId,
                    details: companyBio,
                    logo,
                    route_ids: routeIds,
                    is_wa_optin,
                    cover_picture: coverPicture,
                    established_in: establishedIn ? Number(establishedIn) : undefined,
                    kyc_mandate: mandateKyc,
                    is_aadhaar_verified: aadhaarVerifiedStatus
                })
            }), onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
                if (res.status === 200) {
                    // Need to find alternative way to update mutate function data locally
                    queryClient.invalidateQueries(QueryNames.userProfile);
                    queryClient.invalidateQueries(QueryNames.companyRouteDetails);
                }
            } });
    return { isLoading, isError, isSuccess, mutateAsync };
}

export async function handleUpdateCompanyDetails(
    mutateFn: UseMutateAsyncFunction<Response, unknown, UpdateCompanyDetailsParams, unknown>,
    updateCompanyDetailsParams: UpdateCompanyDetailsParams
) {
    return handleMutateResponse(mutateFn, updateCompanyDetailsParams);
}

export function useGetUserFaq({ companyId }: UserProfileServiceQueryParams, options?: QueryOptions) {
    const newOptions = {
        ...options,
        select: (data: CompanyDetailsData) =>
            data.faqs.map((question) => {
                return { question: question.title, response: question.response, editId: question.id };
            }),
        enabled: true,
        staleTime: Infinity,
        gcTime: Infinity
    };
    return useGetUserProfileDetails({ companyId }, newOptions);
}

interface getLorryByCategoryParams {
    lorry_type: number;
    company_id: number;
}
interface useGetLorryByCategorySignature {
    params: getLorryByCategoryParams;
    options: QueryOptions;
}
export function useGetLorryByCategory({ params, options }: useGetLorryByCategorySignature) {
    const queryClient = useQueryClient();
    const { lorry_type, company_id } = params;
    return useVahakQuery([QueryNames.userLorries.getLorriesByCategory, company_id, lorry_type], () => {
            const validatedCompanyId = validateAndConvertReqParamToInt("company_id", company_id);
            const validatedLorryType = validateAndConvertReqParamToInt("lorry_type", lorry_type);
            if (!validatedCompanyId || !validatedLorryType) {
                return { data: [] };
            }
            return fetch(
                `${API.GET_LORRIES_BY_CATEGORY}${getAuthIdParam()}&c_i=${validatedCompanyId}&t=${validatedLorryType}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        ...getAuthHeader()
                    }
                }
            )
                .then((res) => {
                    if (res.status === 401) {
                        queryClient.invalidateQueries(QueryNames.validateToken);
                        throw new Error("Some Error Occurred");
                    }
                    if (res.status >= 400) {
                        throw new Error("Some Error Occurred");
                    } else {
                        return res.json();
                    }
                })
                .catch(() => {
                    throw new Error("Service Unavailable");
                });
        }, {
            ...options,
            onSuccess: options?.onSuccess ?? (() => {}),
            onError: options?.onError ?? (() => {})
        } as any);
}

interface addEditFaqBodyTypes {
    companyId: number;
    questionId: number;
    response: string;
}

// To Add Faq
export function useAddFaq() {
    const queryClient = useQueryClient();

    const apiClient = `${API.USER_PROFILE_ADD_FAQ}${getAuthIdParam()}`;

    // Removed data and error props to remove warning, can be addded when need arises
    const { isPending: isLoading, isError, isSuccess, mutateAsync } = useMutation({ mutationFn: ({ companyId, questionId, response }: addEditFaqBodyTypes) =>
            fetch(apiClient, {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: JSON.stringify({
                    company_id: companyId,
                    queries: [
                        {
                            id: questionId,
                            response
                        }
                    ]
                })
            }), onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
            } });
    return { isLoading, isError, isSuccess, mutateAsync };
}

export async function handleAddFaq(
    mutateFn: UseMutateAsyncFunction<Response, unknown, addEditFaqBodyTypes, unknown>,
    addFaqBody: addEditFaqBodyTypes
) {
    return handleMutateResponse(mutateFn, addFaqBody);
}

// To Edit Faq
export function useEditFaq() {
    const queryClient = useQueryClient();

    const apiClient = `${API.USER_PROFILE_EDIT_FAQ}${getAuthIdParam()}`;

    // Removed data and error props to remove warning, can be addded when need arises
    const { isPending: isLoading, isError, isSuccess, mutateAsync } = useMutation({ mutationFn: ({ companyId, questionId, response }: addEditFaqBodyTypes) =>
            fetch(apiClient, {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: JSON.stringify({
                    company_id: companyId,
                    id: questionId,
                    response
                })
            }), onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
            } });
    return { isLoading, isError, isSuccess, mutateAsync };
}

export async function handleEditFaq(
    mutateFn: UseMutateAsyncFunction<Response, unknown, addEditFaqBodyTypes, unknown>,
    addFaqBody: addEditFaqBodyTypes
) {
    return handleMutateResponse(mutateFn, addFaqBody);
}

/*
 * Get All Office Address
 */
// interface GetAllAddressQueryParams {
//     companyId: number | string | undefined;
// }

// export function useGetAllAddress({ companyId }: GetAllAddressQueryParams, options?: QueryOptions) {
//     const queryClient = useQueryClient();
//     return useQuery({ queryKey: //         [QueryNames.companyAddressList], queryFn: //         () =>
//             fetch(`${API.ALL_OFFICE_ADDRESS}${getAuthIdParam()}&c_i=${companyId}`, {
//                 method: "GET",
//                 headers: {
//                     Accept: "application/json",
//                     "Content-Type": "application/json",
//                     ...getAuthHeader()
//                 }
//             })
//                 .then((res) => {
//                     if (res.status === 401) {
//                         queryClient.invalidateQueries(QueryNames.validateToken);
//                         throw new Error("Service Unavailable");
//                     } else if (res.status >= 400) throw new Error("Service Unavailable");
//                     else {
//                         return res.json().then((jsonData) => {
//                             const allData = jsonData.data;
//                             return allData;
//                         });
//                     }
//                 })
//                 .catch(() => {
//                     throw new Error("Service Unavailable");
//                 }), ...//         {
//             enabled: options?.enabled,
//             onSuccess: options?.onSuccess,
//             onError: options?.onError,
//             refetchInterval: options?.refetchInterval,
//             staleTime: options?.staleTime,
//             gcTime: options?.cacheTime,
//             select: options?.select,
//             retry: options?.retry
//         }
// });
// }

// Add or Edit address
interface AddOrEditOfficeAddressParams {
    formattedAddress: string;
    landmark: string;
    companyId: number | string | undefined;
    pincode: string;
    placeId: string;
    id?: number;
    is_main: boolean;
}

// To Add or Update Company Address
export function useAddOrEditOfficeAddress() {
    const queryClient = useQueryClient();

    const apiClient = `${API.ADD_EDIT_OFFICE_ADDRESS}${getAuthIdParam()}`;

    const { isPending: isLoading, isError, isSuccess, mutateAsync } = useMutation({ mutationFn: ({ companyId, formattedAddress, landmark, pincode, placeId, id, is_main }: AddOrEditOfficeAddressParams) =>
            fetch(apiClient, {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: JSON.stringify({
                    company_id: parseInt(companyId as string),
                    place_id: placeId,
                    formatted_address: formattedAddress,
                    id,
                    landmark,
                    pincode,
                    is_main
                })
            }), onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
                if (res.status === 200) {
                    queryClient.invalidateQueries(QueryNames.companyAddressList);
                }
            } });
    return { isLoading, isError, isSuccess, mutateAsync };
}

export async function handleAddOrEditOfficeAddress(
    mutateFn: UseMutateAsyncFunction<Response, unknown, AddOrEditOfficeAddressParams, unknown>,
    addOrEditOfficeAddressParams: AddOrEditOfficeAddressParams
) {
    return handleMutateResponse(mutateFn, addOrEditOfficeAddressParams);
}

// Delete address
interface DeleteOfficeAddressParams {
    addressId: number;
}

// To Delete Company Address
export function useDeleteOfficeAddress() {
    const queryClient = useQueryClient();

    const apiClient = `${API.DELETE_OFFICE_ADDRESS}${getAuthIdParam()}&ad_i=`;

    const { isPending: isLoading, isError, isSuccess, mutateAsync } = useMutation({ mutationFn: ({ addressId }: DeleteOfficeAddressParams) =>
            fetch(apiClient + addressId, {
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...getAuthHeader()
                }
            }), onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
                if (res.status === 200) {
                    queryClient.invalidateQueries(QueryNames.companyAddressList);
                }
            } });
    return { isLoading, isError, isSuccess, mutateAsync };
}

export async function handleDeleteOfficeAddress(
    mutateFn: UseMutateAsyncFunction<Response, unknown, DeleteOfficeAddressParams, unknown>,
    deleteOfficeAddressParams: DeleteOfficeAddressParams
) {
    return handleMutateResponse(mutateFn, deleteOfficeAddressParams);
}

// Upload image
interface UploadImageParams {
    formData?: FormData;
}

// Upload profile picture
export function useUploadImage() {
    const queryClient = useQueryClient();

    const apiClient = `${API.UPLOAD_IMAGE}${getAuthIdParam()}`;

    const { isPending: isLoading, isError, isSuccess, mutateAsync } = useMutation({ mutationFn: ({ formData }: UploadImageParams) =>
            fetch(apiClient, {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: formData
            }), onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
                if (res.status === 200) {
                    queryClient.invalidateQueries(QueryNames.userProfile);
                }
            } });
    return { isLoading, isError, isSuccess, mutateAsync };
}

export async function handleUploadImage(
    mutateFn: UseMutateAsyncFunction<Response, unknown, UploadImageParams, unknown>,
    UploadImageParams: UploadImageParams
) {
    return handleMutateResponse(mutateFn, UploadImageParams);
}

/*
 * Get Company Review data
 */
interface CompanyReviewDetailsServiceQueryParams {
    companyId: number;
}

export function useGetCompanyReviewDetails(
    { companyId }: CompanyReviewDetailsServiceQueryParams,
    options?: QueryOptions
) {
    const queryClient = useQueryClient();
    return useVahakQuery([QueryNames.companyReviewList], () =>
            fetch(`${API.COMPANY_REVIEW_DETAILS}${getAuthIdParam()}&c_i=${companyId}`, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...getAuthHeader()
                }
            }).then((res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                    throw new Error("Service not available");
                } else {
                    return res.json().then((jsonData) => {
                        const allData = jsonData.data;
                        return allData;
                    });
                }
            }), {
            ...options,
            onSuccess: options?.onSuccess ?? (() => {}),
            onError: options?.onError ?? (() => {})
        } as any);
}

/*
 * Get Company Route Details
 */
interface CompanyRouteDetailsServiceQueryParams {
    companyId: number;
}

export function useGetCompanyRouteDetails(
    { companyId }: CompanyRouteDetailsServiceQueryParams,
    options?: QueryOptions
) {
    const queryClient = useQueryClient();
    return useVahakQuery([QueryNames.companyRouteDetails], () =>
            fetch(`${API.COMPANY_ROUTE_DETAILS}${getAuthIdParam()}&c_i=${companyId}`, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...getAuthHeader()
                }
            })
                .then((res) => {
                    if (res.status === 401) {
                        queryClient.invalidateQueries(QueryNames.validateToken);
                        throw new Error("Service Unavailable");
                    } else if (res.status >= 400) {
                        throw new Error("Service Unavailable");
                    } else {
                        return res.json().then((jsonData) => {
                            const allData = jsonData.data;
                            return allData;
                        });
                    }
                })
                .catch((err) => {
                    throw new Error(err);
                }), {
            ...options,
            onSuccess: options?.onSuccess ?? (() => {}),
            onError: options?.onError ?? (() => {})
        } as any);
}

// Update company route
interface UpdateCompanyRouteDetailsParams {
    routeIds: number[];
    companyId: number;
}

// Update company route
export function useUpdateCompanyRouteDetails() {
    const queryClient = useQueryClient();

    const apiClient = `${API.UPDATE_COMPANY_ROUTES}${getAuthIdParam()}`;

    const { isPending: isLoading, isError, isSuccess, mutateAsync } = useMutation({ mutationFn: ({ routeIds, companyId }: UpdateCompanyRouteDetailsParams) =>
            fetch(apiClient, {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: JSON.stringify({
                    route_ids: routeIds,
                    company_id: companyId
                })
            }), onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
                if (res.status === 200) {
                    queryClient.invalidateQueries(QueryNames.companyRouteDetails);
                }
            } });
    return { isLoading, isError, isSuccess, mutateAsync };
}

export async function handleUpdateCompanyRouteDetails(
    mutateFn: UseMutateAsyncFunction<Response, unknown, UpdateCompanyRouteDetailsParams, unknown>,
    UpdateCompanyRouteDetailsParams: UpdateCompanyRouteDetailsParams
) {
    return handleMutateResponse(mutateFn, UpdateCompanyRouteDetailsParams);
}
